/**
 * Pop-with-fallback navigation for close/dismiss/cancel affordances (follow-up
 * to audit UIUX-01).
 *
 * Close buttons must POP history, never push: `navigate({ to: parent })` from
 * a close handler pushes the parent onto the stack, so browser back then
 * ping-pongs between the closed surface and its parent instead of leaving.
 * When there is in-app history, `router.history.back()` returns the user to
 * wherever they actually came from. Deep links (Telegram/WhatsApp/LINE/SMS
 * previews, shared URLs) are routinely cold-opened with no in-app history, so
 * the handler falls back to the surface's logical parent — with
 * `replace: true`, so a cold-opened form or list never grows the stack either.
 */
import { useCallback } from 'react';
import { useNavigate, useRouter } from '@tanstack/react-router';

/** Where to land when there is no in-app history to pop. */
export interface SmartBackFallback {
  to: string;
  params?: Record<string, string>;
  search?: Record<string, unknown>;
}

/**
 * Back/close handler that pops in-app history when it exists and replaces to
 * the fallback route on a cold open.
 */
export function useSmartBack(fallback: SmartBackFallback): () => void {
  const router = useRouter();
  const navigate = useNavigate();

  const { to, params, search } = fallback;

  return useCallback(() => {
    if (router.history.canGoBack()) {
      router.history.back();
      return;
    }
    // Only pass what the fallback declares: explicit `params: undefined` /
    // `search: undefined` keys would ride along on navigations that don't
    // need them.
    void navigate({
      to,
      ...(params ? { params } : {}),
      ...(search ? { search } : {}),
      // Replace, never push: the cold-opened page has nothing behind it in the
      // stack, and pushing the fallback would let browser back re-enter it.
      replace: true,
    });
  }, [router, navigate, to, params, search]);
}
